import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext(null);

// Login is OPTIONAL here — guest checkout is a first-class path. This
// context just tracks whether someone happens to be signed in, for order
// history and the admin gate; nothing in the shopping flow requires it.
//
// Admin gate reads profiles.is_shinyvault_admin, NOT is_admin. Trainer Center
// and Shiny Vault share one database and one account per person, but the admin
// privilege is separate per app — a Trainer Center admin has no business in
// this back office and vice versa. See the split_app_admin_privileges migration.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInfluencer, setIsInfluencer] = useState(false);
  const [isConsignor, setIsConsignor] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const profileFetchRef = useRef(null);

  // Session listener ONLY sets state — no database calls in here. A query
  // inside onAuthStateChange blocks Supabase's internal lock and hangs auth
  // (see AUTH_PLAYBOOK.md). The profile fetch lives in its own effect below.
  useEffect(() => {
    // getSession() reads localStorage and does NOT contact the server, so it
    // hands back a session whose access token expired hours ago. That is how
    // the Fulfillment board came to render a logged-in shell for someone the
    // edge functions considered signed out: cached user, dead token, every
    // "Charge & buy label" answering 401 "Not signed in" (Chase 2026-09-02).
    // Validate once on load and clear a genuinely dead session so the admin
    // gate routes to /login on a plain refresh.
    let mounted = true;
    supabase.auth.getSession().then(async ({ data }) => {
      const cached = data?.session?.user || null;
      if (!cached) {
        if (mounted) { setUser(null); setIsLoading(false); }
        return;
      }
      // AUTH_PLAYBOOK: every Supabase call on the auth path gets a 5s timeout,
      // or a hung request leaves the app stuck on "Loading…" forever. Losing
      // the race keeps the cached session — the point is never to block boot.
      const timeout = new Promise((resolve) =>
        setTimeout(() => resolve({ timedOut: true }), 5000));
      try {
        const res = await Promise.race([supabase.auth.getUser(), timeout]);
        if (!mounted) return;
        if (res?.timedOut) {
          setUser(cached);
        } else if (res?.data?.user) {
          setUser(res.data.user);
        } else if (res?.error?.status === 401 || res?.error?.status === 403) {
          // Genuinely rejected, not offline. Drop the stale token so the admin
          // gate routes to /login instead of rendering a shell that 401s.
          // Leave a breadcrumb so the login screen can say WHY they landed
          // there. Being silently bounced to /login reads like a bug.
          try { sessionStorage.setItem('sv_session_expired', '1'); } catch (_e) { /* private mode */ }
          try { await supabase.auth.signOut(); } catch (_e) { /* already gone */ }
          if (mounted) setUser(null);
        } else {
          // Network blip or server hiccup — do not log someone out mid-shift
          // over a dropped request.
          setUser(cached);
        }
      } catch (_e) {
        if (mounted) setUser(cached);
      }
      if (mounted) setIsLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      setIsLoading(false);
    });

    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  // Separate effect keyed on the user id: safe place to hit the database.
  useEffect(() => {
    const uid = user?.id;
    if (!uid) { setIsAdmin(false); setIsInfluencer(false); return; }

    if (profileFetchRef.current) profileFetchRef.current.cancelled = true;
    const fetchState = { cancelled: false };
    profileFetchRef.current = fetchState;

    (async () => {
      const { data, error } = await supabase
        .from('profiles').select('is_shinyvault_admin, is_shinyvault_influencer, is_shinyvault_consignor').eq('id', uid).maybeSingle();
      if (fetchState.cancelled) return;
      if (error) {
        console.error('[auth] profile fetch failed:', error.message);
        setIsAdmin(false); setIsInfluencer(false); return;
      }
      setIsAdmin(!!data?.is_shinyvault_admin);
      setIsInfluencer(!!data?.is_shinyvault_influencer);
      // Consignor = someone whose own product is sold here and who gets paid out.
      // Today that is Olin. It is what puts My Banking in the menu.
      setIsConsignor(!!data?.is_shinyvault_consignor);
    })();

    return () => { fetchState.cancelled = true; };
  }, [user?.id]);

  const signIn = (email, password) => supabase.auth.signInWithPassword({ email, password });
  const signUp = (email, password) => supabase.auth.signUp({ email, password });
  const signOut = () => supabase.auth.signOut();

  return (
    <AuthContext.Provider value={{
      user, isAdmin, isInfluencer, isConsignor, isLoading, signIn, signUp, signOut,
      // The influencer pipeline is Chase's alone — not a staff-wide area.
      isOwner: (user?.email || '').toLowerCase() === 'thek2way17@gmail.com',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
